import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUserLanguages } from '@/features/voice-campaigns/api';
import { cn } from '@/lib/utils/cn';
import {
  TTS_TEMPLATES,
  type Gender,
  type TtsFlowFormValues,
  type TtsTemplateId,
} from '../../types';

interface Props {
  values: TtsFlowFormValues;
  setValues: (patch: Partial<TtsFlowFormValues>) => void;
  /** Block the template picker when editing an existing flow. */
  lockTemplate?: boolean;
  autoFocusName?: boolean;
}

export function StepBasics({
  values,
  setValues,
  lockTemplate,
  autoFocusName,
}: Props) {
  const languages = useUserLanguages();

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Template</CardTitle>
          <CardDescription>
            Pick the shape of this flow. Locked after creation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {TTS_TEMPLATES.map((t) => {
              const picked = values.templateId === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  disabled={lockTemplate}
                  onClick={() =>
                    setValues({ templateId: t.value as TtsTemplateId })
                  }
                  className={cn(
                    'rounded-lg border bg-card p-3 text-left transition-all',
                    picked
                      ? 'border-primary ring-2 ring-primary/30 ring-offset-1'
                      : 'border-border hover:border-primary/40',
                    lockTemplate && !picked && 'cursor-not-allowed opacity-40',
                  )}
                >
                  <div className="text-sm font-semibold text-foreground">
                    {t.label}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t.description}
                  </p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Basics</CardTitle>
          <CardDescription>
            Give the flow a name and pick the voice it speaks with.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="flow-name">
              Flow name
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="flow-name"
              value={values.ttsFlowName}
              onChange={(e) => setValues({ ttsFlowName: e.target.value })}
              autoFocus={autoFocusName}
              placeholder="e.g. WelcomePromptHindi"
            />
          </div>
          <div className="space-y-2">
            <Label>
              Language
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Select
              value={values.language}
              onValueChange={(v) => setValues({ language: v })}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    languages.isLoading
                      ? 'Loading…'
                      : (languages.data ?? []).length === 0
                        ? 'No languages available'
                        : 'Pick a language'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(languages.data ?? []).map((l, i) => (
                  <SelectItem key={`${l.language}-${i}`} value={l.languageCode ?? l.language}>
                    {l.language}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>
              Gender
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Select
              value={values.gender}
              onValueChange={(v) => setValues({ gender: v as Gender })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Pick a voice" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MALE">Male</SelectItem>
                <SelectItem value="FEMALE">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
